import { RefObject, useEffect, useRef } from "react";
import { Nullable } from "utils/typeCheck.utils";

type ClickOutsideEvent = MouseEvent | TouchEvent;
type Handler = (event: ClickOutsideEvent) => void;
type EventTarget = Window | ShadowRoot;

/**
 * Parameters for the useOnClickOutside hook
 * @template T - The type of HTML element being referenced
 */
interface UseOnClickOutsideParams<T extends HTMLElement> {
  /** Primary element ref to detect clicks outside of */
  ref: RefObject<T>;
  /** Callback function invoked when a click outside is detected */
  handler: Handler;
  /** Optional shadow root for Shadow DOM support */
  shadowRoot?: Nullable<ShadowRoot>;
  /** Optional array of refs to exclude from outside click detection */
  excludeRefs?: ReadonlyArray<RefObject<HTMLElement>>;
  /** Whether to use capture phase for event listeners (default: true) */
  useCapture?: boolean;
  /** Optional additional dependencies that trigger effect re-subscription */
  triggerDependencies?: unknown[];
}

const EVENTS = ["mousedown", "touchstart"] as const;
const LISTENER_OPTIONS: AddEventListenerOptions = { capture: true };

/**
 * Custom hook that detects clicks outside specified element(s)
 *
 * Features:
 * - Shadow DOM support via composedPath API
 * - Multiple excluded refs support
 * - Stable handler reference to prevent unnecessary re-renders
 * - Capture phase event handling for proper event ordering
 *
 * @example
 * ```tsx
 * const dropdownRef = useRef<HTMLDivElement>(null);
 * const buttonRef = useRef<HTMLButtonElement>(null);
 *
 * useOnClickOutside({
 *   ref: dropdownRef,
 *   handler: () => setIsOpen(false),
 *   excludeRefs: [buttonRef]
 * });
 * ```
 */
export const useOnClickOutside = <T extends HTMLElement = HTMLDivElement>(params: UseOnClickOutsideParams<T>): void => {
  const { ref, handler, shadowRoot, excludeRefs = [], useCapture = true, triggerDependencies = [] } = params;

  // Store handler and excludeRefs in refs to maintain stable effect dependencies
  // This prevents unnecessary listener re-subscriptions when these values change
  const handlerRef = useRef<Handler>(handler);
  const excludeRefsRef = useRef<ReadonlyArray<RefObject<HTMLElement>>>(excludeRefs);

  // Keep handler ref in sync with latest handler
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  // Keep excludeRefs ref in sync with latest excludeRefs
  useEffect(() => {
    excludeRefsRef.current = excludeRefs;
  }, [excludeRefs]);

  useEffect(() => {
    // Early return if ref is not available
    if (!ref.current) {
      return;
    }

    /**
     * Checks if a node is inside the primary ref or any excluded refs
     */
    const isInsideRef = (node: Node | null): boolean => {
      if (!node) return false;
      if (ref.current?.contains(node)) return true;
      return excludeRefsRef.current.some((excludeRef) => excludeRef.current?.contains(node));
    };

    /**
     * Event handler for click outside detection
     */
    const handleClickOutside = (event: Event): void => {
      // Modern browsers: Check entire event path for Shadow DOM support
      const path = event.composedPath?.();
      if (path?.some((node) => node instanceof Node && isInsideRef(node))) {
        return;
      }

      // Fallback: Check event target directly for browsers without composedPath
      if (event.target instanceof Node && isInsideRef(event.target)) {
        return;
      }

      handlerRef.current(event as ClickOutsideEvent);
    };

    // Determine event targets based on shadow root presence
    const targets: EventTarget[] = shadowRoot ? [window, shadowRoot] : [window];
    const options = useCapture ? LISTENER_OPTIONS : undefined;

    // Attach event listeners
    targets.forEach((target) => {
      EVENTS.forEach((eventType) => {
        target.addEventListener(eventType, handleClickOutside, options);
      });
    });

    // Cleanup function
    return () => {
      targets.forEach((target) => {
        EVENTS.forEach((eventType) => {
          target.removeEventListener(eventType, handleClickOutside, options);
        });
      });
    };
  }, [ref, shadowRoot, useCapture, ...triggerDependencies]);
};
